import axios from "axios";
import router from "@/router";
import { classifyApiError } from "@/services/useApiError";

const axiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  withCredentials: true,
  // Without this a hung request never rejects and the view sits on "Loading..." forever —
  // the same dead end as a swallowed error, reached a different way.
  timeout: 20000,
});

let isRedirecting = false;

/**
 * Where to send the user back after they sign in again. Kept relative so it can never point
 * off-site, and dropped for the login pages themselves.
 */
const returnPathFor = (target: string): string | undefined => {
  const current = window.location.pathname + window.location.search;
  if (
    current === target ||
    current.startsWith("/login") ||
    current.startsWith("/admin/login")
  ) {
    return undefined;
  }
  return current;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only an expired or missing session redirects. A 403 now belongs to the caller, which
    // renders it in place so the URL survives; a 5xx belongs to the caller too, or a backend
    // hiccup would look to the user like being logged out.
    if (classifyApiError(error).kind === "unauthenticated") {
      const isRoleCheck = error.config?.url?.endsWith("/user/role");
      const currentPath = window.location.pathname;
      const target = currentPath.startsWith("/admin")
        ? "/admin/login"
        : "/login";

      if (!isRoleCheck && !isRedirecting && currentPath !== target) {
        isRedirecting = true;
        const redirect = returnPathFor(target);
        setTimeout(() => {
          router
            .replace({
              path: target,
              query: { expired: "1", ...(redirect ? { redirect } : {}) },
            })
            .finally(() => {
              isRedirecting = false;
            });
        }, 0);
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
